import React, { useEffect, useState } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Switch } from "../../components/ui/switch";
import { Clock, Users, Plus, Save, Building } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { ROLES } from "../../lib/statuses";
import { useAuth } from "../../context/AuthContext";
import { DataExportCenter } from "../../components/admin/DataExportCenter";

export default function Settings() {
  const { hasRole } = useAuth();
  const isSuper = hasRole("SUPER_ADMIN");
  const [threshold, setThreshold] = useState(14);
  const [sender, setSender] = useState(null);
  const [users, setUsers] = useState([]);
  const [userOpen, setUserOpen] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", name: "", role: "WARRANTY_STAFF", password: "" });

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setThreshold(data.overdue_threshold_days); setSender(data.sender || {}); });
    if (isSuper) api.get("/auth/users").then(({ data }) => setUsers(data));
  }, [isSuper]);

  const saveThreshold = async () => {
    try { await api.put("/admin/settings", { overdue_threshold_days: Number(threshold) }); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSender = async () => {
    try { await api.put("/admin/settings", { sender }); toast.success("Sender address saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createUser = async () => {
    if (!userForm.email || !userForm.name || !userForm.password) { toast.error("All fields required"); return; }
    try {
      await api.post("/auth/users", userForm);
      toast.success("User created");
      setUserOpen(false);
      setUserForm({ email: "", name: "", role: "WARRANTY_STAFF", password: "" });
      const { data } = await api.get("/auth/users"); setUsers(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/auth/users/${u.id}`, { active: !u.active }); const { data } = await api.get("/auth/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Settings">
      <div className="max-w-2xl space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-1 flex items-center gap-2 font-display text-base font-semibold text-slate-900"><Clock className="h-4 w-4 text-blue-600" /> Vendor Claim Aging</h3>
          <p className="mb-4 text-sm text-slate-500">Vendor claims pending beyond this threshold are flagged as overdue.</p>
          <div className="flex items-end gap-3">
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Overdue Threshold (days)</Label>
              <Input data-testid="threshold-input" type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-40" />
            </div>
            <Button onClick={saveThreshold} data-testid="save-threshold-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Save className="h-4 w-4" /> Save</Button>
          </div>
        </div>

        {isSuper && sender && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sender-settings">
            <h3 className="mb-1 flex items-center gap-2 font-display text-base font-semibold text-slate-900"><Building className="h-4 w-4 text-blue-600" /> Shipping Sender Address (FROM)</h3>
            <p className="mb-4 text-sm text-slate-500">Used in the FROM section of every Shipping Slip.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ["company_name", "Company Name"], ["brand_name", "Brand / Display Name"], ["contact_person", "Contact Person"],
                ["phone", "Phone"], ["street", "Street"], ["area", "Area"], ["city", "City"], ["province", "Province"],
                ["postal", "Postal / PO"], ["country", "Country"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label className="mb-1.5 block text-sm font-medium">{label}</Label>
                  <Input data-testid={`sender-${k}`} value={sender[k] || ""} onChange={(e) => setSender({ ...sender, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <Button onClick={saveSender} data-testid="save-sender-btn" className="mt-4 gap-1.5 bg-blue-600 hover:bg-blue-700"><Save className="h-4 w-4" /> Save Sender Address</Button>
          </div>
        )}

        {isSuper && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-900"><Users className="h-4 w-4 text-blue-600" /> Users & Roles</h3>
              <Button onClick={() => setUserOpen(true)} data-testid="add-user-btn" size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Add User</Button>
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Active</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{u.role.replace(/_/g, " ")}</span></TableCell>
                    <TableCell><Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {hasRole("SUPER_ADMIN", "WARRANTY_MANAGER", "FINANCE") && <DataExportCenter />}
      </div>

      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block text-sm font-medium">Name</Label><Input data-testid="user-name" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} /></div>
            <div><Label className="mb-1.5 block text-sm font-medium">Email</Label><Input data-testid="user-email" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} /></div>
            <div><Label className="mb-1.5 block text-sm font-medium">Role</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-sm font-medium">Password</Label><Input data-testid="user-password" type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserOpen(false)}>Cancel</Button>
            <Button onClick={createUser} data-testid="save-user-btn" className="bg-blue-600 hover:bg-blue-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
